import React, { useCallback, useEffect, useRef, useState } from 'react';
import RecommendedTrackCard from './RecommendedTrackCard';
import { trackKey } from './trackCardUtils';
import styles from './RecommendedTracksSection.module.css';

const TRACK_CAROUSEL_QUERY = '(max-width: 768px)';

function TrackCarouselArrow({ direction }) {
  const points = direction === 'left' ? 'M19 12H5M11 6l-6 6 6 6' : 'M5 12h14M13 6l6 6-6 6';
  return (
    <svg className={styles.trackSwapIcon} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        d={points}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function useTrackCarousel(slideCount, resetToken) {
  const scrollerRef = useRef(null);
  const indexRef = useRef(0);
  const programmaticRef = useRef(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isCarousel, setIsCarousel] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(TRACK_CAROUSEL_QUERY).matches,
  );

  const syncIndex = useCallback((next) => {
    const maxIndex = Math.max(slideCount - 1, 0);
    const clamped = Math.max(0, Math.min(maxIndex, next));
    indexRef.current = clamped;
    setActiveIndex((current) => (current === clamped ? current : clamped));
  }, [slideCount]);

  const readClosest = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return 0;
    const origin = el.getBoundingClientRect().left;
    let closest = 0;
    let min = Infinity;
    [...el.children].forEach((slide, index) => {
      const delta = Math.abs(slide.getBoundingClientRect().left - origin);
      if (delta < min) {
        min = delta;
        closest = index;
      }
    });
    return closest;
  }, []);

  useEffect(() => {
    indexRef.current = 0;
    setActiveIndex(0);
    scrollerRef.current?.scrollTo({ left: 0, behavior: 'auto' });
  }, [slideCount, resetToken]);

  useEffect(() => {
    const media = window.matchMedia(TRACK_CAROUSEL_QUERY);
    const el = scrollerRef.current;
    const applyMode = () => {
      setIsCarousel(media.matches);
      if (media.matches) syncIndex(readClosest());
    };
    const syncFromScroll = () => {
      if (!media.matches) return;
      if (programmaticRef.current) {
        syncIndex(indexRef.current);
        return;
      }
      syncIndex(readClosest());
    };
    const finishScroll = () => {
      programmaticRef.current = false;
      if (media.matches) syncIndex(readClosest());
    };
    applyMode();
    media.addEventListener('change', applyMode);
    el?.addEventListener('scroll', syncFromScroll, { passive: true });
    el?.addEventListener('scrollend', finishScroll);
    window.addEventListener('resize', syncFromScroll);
    return () => {
      media.removeEventListener('change', applyMode);
      el?.removeEventListener('scroll', syncFromScroll);
      el?.removeEventListener('scrollend', finishScroll);
      window.removeEventListener('resize', syncFromScroll);
    };
  }, [readClosest, syncIndex]);

  const goTo = useCallback((next) => {
    const el = scrollerRef.current;
    if (!el || !window.matchMedia(TRACK_CAROUSEL_QUERY).matches) return;
    const maxIndex = Math.max(slideCount - 1, 0);
    const clamped = Math.max(0, Math.min(maxIndex, next));
    const slide = el.children[clamped];
    if (!slide) return;
    programmaticRef.current = true;
    syncIndex(clamped);
    const left = slide.getBoundingClientRect().left - el.getBoundingClientRect().left + el.scrollLeft;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollTo({ left, behavior: reduce ? 'auto' : 'smooth' });
    window.setTimeout(() => {
      if (!programmaticRef.current) return;
      programmaticRef.current = false;
      syncIndex(indexRef.current);
    }, 520);
  }, [slideCount, syncIndex]);

  return { scrollerRef, activeIndex, isCarousel, goTo, indexRef };
}

function trackTitle(track) {
  return String(track?.title ?? '').trim() || 'Untitled track';
}

export function TrackCardList({
  tracks = [],
  selectedTrackId = '',
  onSelectTrack,
  onLockedTrack,
  label = 'Quick Richuels',
  carousel = true,
}) {
  const list = Array.isArray(tracks) ? tracks : [];
  const slideKey = list.map((track, index) => trackKey(track, index)).join('\0');
  const { scrollerRef, activeIndex, isCarousel, goTo, indexRef } = useTrackCarousel(list.length, slideKey);
  const showControls = carousel && isCarousel && list.length > 1;

  if (list.length === 0) return null;

  return (
    <div className={styles.trackCarousel}>
      <div
        className={styles.scroller}
        ref={scrollerRef}
        role={showControls ? 'region' : 'list'}
        aria-roledescription={showControls ? 'carousel' : undefined}
        aria-label={showControls ? label : undefined}
        tabIndex={showControls ? 0 : undefined}
        onKeyDown={showControls ? (event) => {
          if (event.key === 'ArrowRight') {
            event.preventDefault();
            goTo(indexRef.current + 1);
          } else if (event.key === 'ArrowLeft') {
            event.preventDefault();
            goTo(indexRef.current - 1);
          }
        } : undefined}
      >
        {list.map((track, index) => (
          <RecommendedTrackCard
            key={trackKey(track, index)}
            track={track}
            index={index}
            selectedTrackId={selectedTrackId}
            onSelectTrack={onSelectTrack}
            onLockedTrack={onLockedTrack}
            isSlide={showControls}
            slideCount={list.length}
          />
        ))}
      </div>
      {showControls && (
        <div className={styles.trackNav}>
          <button
            type="button"
            className={styles.trackSwap}
            aria-label={`Previous ${label}`}
            disabled={activeIndex === 0}
            onClick={() => goTo(activeIndex - 1)}
          >
            <TrackCarouselArrow direction="left" />
          </button>
          <div className={styles.trackDots}>
            {list.map((track, index) => (
              <button
                key={trackKey(track, index)}
                type="button"
                className={index === activeIndex ? `${styles.trackDot} ${styles.trackDotActive}` : styles.trackDot}
                aria-label={trackTitle(track)}
                aria-current={index === activeIndex ? 'true' : undefined}
                onClick={() => goTo(index)}
              />
            ))}
          </div>
          <button
            type="button"
            className={styles.trackSwap}
            aria-label={`Next ${label}`}
            disabled={activeIndex >= list.length - 1}
            onClick={() => goTo(activeIndex + 1)}
          >
            <TrackCarouselArrow direction="right" />
          </button>
          <p className={styles.trackLive} aria-live="polite">
            {trackTitle(list[activeIndex])}, {activeIndex + 1} of {list.length}
          </p>
        </div>
      )}
    </div>
  );
}

function RecommendedTracksSection({
  tracks = [],
  recommendedCategory = '',
  completedCount = 0,
  totalCount = 0,
  selectedTrackId = '',
  onSelectTrack,
  onLockedTrack,
  showProgress = true,
  title = 'Recommended',
  subtitle = 'Your next best money moves. Start any track.',
  emptyMessage = '',
  compact = false,
}) {
  const list = Array.isArray(tracks) ? tracks : [];
  const total = totalCount > 0 ? totalCount : list.length;
  const completed = Math.max(0, Math.min(completedCount, total));
  const percent = total > 0 ? Math.round((completed / total) * 100) : 0;
  const categoryLabel = compact ? '' : String(recommendedCategory ?? '').trim();
  const showSubtitle = Boolean(subtitle) && !compact;

  const header = (
    <div className={styles.header}>
      <div className={styles.heading}>
        <div className={styles.titleRow}>
          <h2 id="recommended-tracks-title" className={styles.title}>
            {title}
          </h2>
          {categoryLabel ? (
            <span className={styles.categoryPill} title={categoryLabel}>{categoryLabel}</span>
          ) : null}
        </div>
        {showSubtitle ? <p className={styles.subtitle}>{subtitle}</p> : null}
      </div>
      {showProgress && list.length > 0 ? (
        <div className={styles.progressWrap}>
          <p className={styles.progressLabel}>
            {completed}
            {' '}
            of
            {' '}
            {total}
            {' '}
            completed
          </p>
          <div
            className={styles.progressBar}
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={total}
            aria-valuenow={completed}
            aria-label="Quick Richuel progress"
          >
            <div className={styles.progressFill} style={{ width: `${percent}%` }} />
          </div>
          <span className={styles.progressPct}>{percent}%</span>
        </div>
      ) : null}
    </div>
  );

  if (list.length === 0) {
    if (!emptyMessage) return null;
    return (
      <section
        className={`${styles.recommendedSection}${compact ? ` ${styles.compact}` : ''}`}
        aria-labelledby="recommended-tracks-title"
      >
        {header}
        <p className={styles.subtitle}>{emptyMessage}</p>
      </section>
    );
  }

  return (
    <section
      className={`${styles.recommendedSection}${compact ? ` ${styles.compact}` : ''}`}
      id="recommended-tracks"
      aria-labelledby="recommended-tracks-title"
    >
      {header}
      <TrackCardList
        tracks={list}
        selectedTrackId={selectedTrackId}
        onSelectTrack={onSelectTrack}
        onLockedTrack={onLockedTrack}
        label={title}
        carousel={!compact}
      />
    </section>
  );
}

export default RecommendedTracksSection;
